import React from 'react';
import { Calendar, MapPin, GraduationCap } from 'lucide-react';
import SectionHeader from './SectionHeader';
import { useLanguage } from '../language/LanguageContext';
import Tag from './ui/Tag';
import MetaItem from './ui/MetaItem';
import Reveal from './ui/Reveal';

const EducationSection = () => {
  const { translations } = useLanguage();
  const { education } = translations;

  return (
    <section>
      <SectionHeader title={education.title} />

      <div className="flex flex-col md:flex-row md:items-start gap-8">
        <div className="md:w-1/3 md:flex-shrink-0">
          <Reveal className="md:sticky md:top-24">
            <div className="card p-5">
              <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-accent/10 border border-accent/25 text-accent mb-4">
                <GraduationCap size={20} />
              </span>

              <h3 className="text-lg font-bold text-dark-heading mb-1.5 leading-snug">
                {education.degree}
              </h3>
              <p className="text-dark-text text-sm mb-4">{education.university}</p>

              <div className="space-y-2.5 pt-4 border-t border-dark-border">
                <MetaItem icon={Calendar}>{education.period}</MetaItem>
                <MetaItem icon={MapPin}>{education.location}</MetaItem>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="md:w-2/3 min-w-0 space-y-8">
          <Reveal delay={80}>
            <h4 className="font-semibold text-dark-heading mb-3">{education.highlights}</h4>
            <p className="text-dark-text leading-relaxed max-w-2xl">{education.description}</p>
          </Reveal>

          <Reveal delay={140}>
            <h4 className="font-semibold text-dark-heading mb-3">{education.coursework}</h4>
            <div className="flex flex-wrap gap-2">
              {education.courses.map((course: string) => (
                <Tag key={course}>{course}</Tag>
              ))}
            </div>
          </Reveal>

          <Reveal delay={200}>
            <h4 className="font-semibold text-dark-heading mb-3">{education.researchProjects}</h4>
            <ul className="space-y-3">
              {education.projects.map((item: string, index: number) => (
                <li key={index} className="card p-4 flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className="w-1.5 h-1.5 rounded-full bg-accent-gradient mt-[0.55rem] flex-shrink-0"
                  />
                  <span className="text-dark-text text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default EducationSection;
